/**
 * Morse — geprüfte Merkwörter (Eselsbrücken) je Buchstabe, dazu eine Merkregel für die Ziffern. (15.10.2026)
 *
 * Martin (21:47): „Ich brauche einen Anhalt, dass ich das richtig erkenne.“ Vespera hatte im Chat spontan Merkwörter
 * genannt (Ra-ke-te für R, Ka-ra-te für K, Mond für M), deren Rhythmus NICHT zum Code passt. Darum gibt es EINE Quelle:
 * diese Datei. Seite (Kacheln, Tabelle, Spickzettel, Kennenlernen) und Vespera (Werkzeug `morse_merkwort`) lesen nur hier.
 *
 * ## Die Regel (die klassische deutsche Merkwort-Methode)
 *
 * Das Merkwort wird in Silben gesprochen; jede Silbe ist ein Ton:
 *   - eine Silbe mit dem Buchstaben „o“ ist ein STRICH (lang),
 *   - jede andere Silbe ist ein PUNKT (kurz).
 * Beispiel: „Mo-tor“ = zwei o-Silben = Strich Strich = M. Diese „o-Regel“ ist die in deutschsprachigen Morse-Lehrgängen
 * übliche Fassung der Merkwörter (Hi-ma-la-ja = H, Co-ca-Co-la = C, Mo-tor = M, No-te = N, Sa-la-mi = S, U-ni-form = U).
 * Geprüft wird an der Schreibung: „o“ (nicht „ö“) in der Silbe.
 *
 * Die übrigen Wörter sind nach dieser Regel AUSGESUCHT, nicht aus einer Liste abgeschrieben — sie gelten, weil die Regel
 * sie nachrechnet: tests/shared/morse-merkwoerter.test.ts trennt jedes Wort an den Bindestrichen (und Leerzeichen),
 * prüft, dass jede Silbe genau EINEN Vokalkern hat (kleiner Silbenzähler, `silbenKerne`), rechnet den Takt nach der
 * o-Regel und vergleicht ihn mit dem gespeicherten Takt UND mit MORSE_TABELLE. Ein Wort, das nicht passt, macht den
 * Test rot (Gegenprobe: Ra-ke-te, Ka-ra-te, Mond fallen durch).
 *
 * Ziffern haben keine Wörter (fünf Silben wären unhandlich), sondern die Bauregel des Codes selbst — ebenfalls
 * nachgerechnet: 1–5 = so viele Punkte vorne wie die Ziffer, Rest Striche; 6–9 = (Ziffer − 5) Striche vorne, Rest
 * Punkte; 0 = fünf Striche.
 *
 * Takt hier wie in MORSE_TABELLE: „.“ = Punkt, „-“ = Strich.
 */
import { MORSE_TABELLE } from "./morse.js";

/** Die Regel in einem Satz — für die Seite und für Vespera. */
export const MORSE_MERKWORT_REGEL = "Jede Silbe ist ein Ton: Silbe mit „o“ = Strich (lang), jede andere Silbe = Punkt (kurz).";
export const MORSE_ZIFFERN_REGEL = "Ziffern 1–5: so viele Punkte vorne wie die Ziffer, der Rest Striche. 6–9: (Ziffer minus 5) Striche vorne, der Rest Punkte. 0: fünf Striche.";

interface MerkwortDaten {
  /** Das Merkwort, Silben mit Bindestrich (Wörter mit Leerzeichen) getrennt. */
  merkwort: string;
  /** Der Takt, den das Merkwort trägt (muss nach der o-Regel herauskommen und dem Code gleichen — Test). */
  takt: string;
}

/** Je Buchstabe A–Z ein Merkwort. */
export const MORSE_MERKWOERTER: Readonly<Record<string, MerkwortDaten>> = {
  A: { merkwort: "Am-boss", takt: ".-" },
  B: { merkwort: "Bo-na-par-te", takt: "-..." },
  C: { merkwort: "Co-ca-Co-la", takt: "-.-." },
  D: { merkwort: "Don-ners-tag", takt: "-.." },
  E: { merkwort: "Elch", takt: "." },
  F: { merkwort: "Feu-er-pro-be", takt: "..-." },
  G: { merkwort: "Go-mor-rha", takt: "--." },
  H: { merkwort: "Hi-ma-la-ja", takt: "...." },
  I: { merkwort: "I-gel", takt: ".." },
  J: { merkwort: "Ja, Pro-to-koll", takt: ".---" },
  K: { merkwort: "Kor-ri-dor", takt: "-.-" },
  L: { merkwort: "Li-mo-na-de", takt: ".-.." },
  M: { merkwort: "Mo-tor", takt: "--" },
  N: { merkwort: "No-te", takt: "-." },
  O: { merkwort: "Or-tho-dox", takt: "---" },
  P: { merkwort: "Phi-lo-so-phie", takt: ".--." },
  Q: { merkwort: "Co-lo-ra-do", takt: "--.-" },
  R: { merkwort: "Re-tor-te", takt: ".-." },
  S: { merkwort: "Sa-la-mi", takt: "..." },
  T: { merkwort: "Tor", takt: "-" },
  U: { merkwort: "U-ni-form", takt: "..-" },
  V: { merkwort: "Vä-ter-chen Frost", takt: "...-" },
  W: { merkwort: "Wie so oft", takt: ".--" },
  X: { merkwort: "Kon-di-ti-on", takt: "-..-" },
  Y: { merkwort: "Yo-ga-kom-fort", takt: "-.--" },
  Z: { merkwort: "Zoll-of-fi-zier", takt: "--.." },
};

/** Die Silben eines Merkworts: getrennt an Bindestrich und Leerzeichen, Satzzeichen weg („Ja, Pro-to-koll“ → Ja Pro to koll). */
export function merkwortSilben(merkwort: string): string[] {
  return merkwort
    .split(/[-\s]+/)
    .map((s) => s.replace(/[^\p{L}]/gu, ""))
    .filter((s) => s.length > 0);
}

/** Die o-Regel für eine Silbe: mit „o“ lang (Strich), sonst kurz (Punkt). */
export function silbeIstLang(silbe: string): boolean {
  return /o/i.test(silbe);
}

/** Der Takt eines Merkworts nach der o-Regel („Re-tor-te“ → „.-.“). */
export function merkwortTakt(merkwort: string): string {
  return merkwortSilben(merkwort)
    .map((s) => (silbeIstLang(s) ? "-" : "."))
    .join("");
}

const VOKALE = "aeiouäöüy";
/** Zwei Buchstaben, die EINEN Vokalkern bilden (Zwielaute und Doppelvokale). */
const KERNE_ZWEI = ["ie", "ei", "ai", "au", "eu", "äu", "aa", "ee", "oo"];

/**
 * Wie viele Vokalkerne hat eine Silbe? Klein gehalten: Vokale von links, ein Zwielaut oder Doppelvokal zählt einmal,
 * „y“ vor einem Vokal ist ein Mitlaut („Yo-ga“). Eine richtig getrennte Silbe hat genau einen Kern.
 */
export function silbenKerne(silbe: string): number {
  const s = [...silbe.toLowerCase()];
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s[i] ?? "";
    const danach = s[i + 1] ?? "";
    if (!VOKALE.includes(c)) continue;
    if (c === "y" && danach !== "" && VOKALE.includes(danach)) continue;
    n++;
    if (danach !== "" && KERNE_ZWEI.includes(c + danach)) i++;
  }
  return n;
}

/** Der Code einer Ziffer nach der Bauregel (nicht aus der Tabelle gelesen — der Test vergleicht beides). */
export function ziffernTakt(ziffer: number): string {
  if (ziffer === 0) return "-----";
  if (ziffer <= 5) return ".".repeat(ziffer) + "-".repeat(5 - ziffer);
  return "-".repeat(ziffer - 5) + ".".repeat(10 - ziffer);
}

export interface MorseMerkSilbe {
  silbe: string;
  lang: boolean;
}

/** Was Seite und Vespera zeigen bzw. sagen. */
export type MorseMerkhilfe =
  | { zeichen: string; art: "wort"; merkwort: string; silben: MorseMerkSilbe[]; takt: string }
  | { zeichen: string; art: "regel"; text: string; takt: string };

const mehrzahl = (n: number, eins: string, viele: string): string => `${n} ${n === 1 ? eins : viele}`;

/** Die Merkhilfe eines Zeichens — `null`, wo es keine geprüfte gibt (Satzzeichen, Umlaute). */
export function morseMerkhilfe(zeichen: string): MorseMerkhilfe | null {
  const z = zeichen.trim().toUpperCase();
  const w = MORSE_MERKWOERTER[z];
  if (w) {
    return { zeichen: z, art: "wort", merkwort: w.merkwort, silben: merkwortSilben(w.merkwort).map((silbe) => ({ silbe, lang: silbeIstLang(silbe) })), takt: w.takt };
  }
  if (/^[0-9]$/.test(z) && MORSE_TABELLE[z] !== undefined) {
    const d = Number(z);
    const takt = ziffernTakt(d);
    const vorne = takt[0] === "." ? "Punkt" : "Strich";
    const n = takt.indexOf(takt[0] === "." ? "-" : ".");
    const zahlVorne = n === -1 ? 5 : n;
    const text =
      zahlVorne === 5
        ? `fünf ${vorne === "Punkt" ? "Punkte" : "Striche"}`
        : `${mehrzahl(zahlVorne, vorne, vorne === "Punkt" ? "Punkte" : "Striche")}, dann ${mehrzahl(5 - zahlVorne, vorne === "Punkt" ? "Strich" : "Punkt", vorne === "Punkt" ? "Striche" : "Punkte")}`;
    return { zeichen: z, art: "regel", text, takt };
  }
  return null;
}

/** Die Merkhilfe als ein kurzer Text („Re-tor-te“ bzw. „1 Punkt, dann 4 Striche“). */
export function merkhilfeText(m: MorseMerkhilfe): string {
  return m.art === "wort" ? m.merkwort : m.text;
}
